/**
 * FloDialog — 공급 흐름도를 큰 다이얼로그로 여는 얇은 셸.
 * 팬·줌·범례·전체 화면·조작 안내는 전부 FloView 안에 있다 — 여기서는 제목·닫기·캡션만 더한다.
 * 중립 leaf — pages/** 를 import 하지 않는다.
 *
 * props
 *   open, onClose  다이얼로그 열림 상태와 닫기 콜백. 전체 화면 중의 Esc 는 전체 화면만 끈다.
 *   title          제목 줄 문구(번역된 문자열).
 *   caption        그림 아래 문구(번역된 문자열). 전체 화면에서는 보이지 않는다.
 *   FloView 로 넘기는 것: graph, lineage, legend, hints, foldLimit, laneTypeOrder, locationLabels
 * 사용법·그래프 계약: saas-web/.claude/skills/flo/SKILL.md
 */
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";

import { FS } from "@plannel/utils/font-util";
import FloView from "@plannel/components/flo/FloView";

export default function FloDialog({
  open, onClose, title, caption, graph, lineage, legend, hints, foldLimit, laneTypeOrder,
  locationLabels,
}) {
  const { t } = useTranslation();

  // Esc 를 눌렀을 때 브라우저 전체 화면이 걸려 있으면 그 Esc 는 전체 화면을 끄는 몫이다.
  // 여기서 같이 다이얼로그까지 닫으면 사용자가 두 번 눌러야 하는 것처럼 보인다.
  const handleClose = (event, reason) => {
    if (reason === "escapeKeyDown" && document.fullscreenElement) return;
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xl">
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
        <Box sx={{
          flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          fontSize: FS.TITLE, fontWeight: 700,
        }}>
          {title}
        </Box>
        <IconButton size="small" onClick={() => onClose?.()} aria-label={t("close")}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, height: "80vh", display: "flex", flexDirection: "column", minHeight: 0 }}>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <FloView graph={graph} height="100%" lineage={lineage} legend={legend} hints={hints}
                   foldLimit={foldLimit} laneTypeOrder={laneTypeOrder} locationLabels={locationLabels} />
        </Box>
        {caption && (
          <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary", px: 1.5, py: 0.5, flexShrink: 0 }}>
            {caption}
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
}
